import { type AbstractMesh, Color3, Mesh, MeshBuilder, type Scene, StandardMaterial, TransformNode } from "@babylonjs/core";
import { AdvancedDynamicTexture, Rectangle, TextBlock } from "@babylonjs/gui";
import type { PlayerState } from "../gen/livesoup/v1/realtime_pb";
import { BODY_HEIGHT, BODY_RADIUS } from "./body";
import { InterpBuffer } from "./interp";

const RENDER_DELAY = 100;
const HEAD = 0.35;

/** The avatar others see is as tall as the body that collides: a capsule with a box head on top (spec 2m §3.6). */
export function avatarShape(): { bodyHeight: number; bodyY: number; head: number; headY: number } {
  const bodyHeight = BODY_HEIGHT - HEAD;
  return { bodyHeight, bodyY: bodyHeight / 2, head: HEAD, headY: BODY_HEIGHT - HEAD / 2 };
}

interface Avatar { root: TransformNode; buf: InterpBuffer; label: Rectangle; name: string }

export class RemotePlayers {
  private avatars = new Map<string, Avatar>();
  private gui: AdvancedDynamicTexture;

  constructor(private readonly scene: Scene, private readonly onMeshes?: (meshes: AbstractMesh[], added: boolean) => void) {
    this.gui = AdvancedDynamicTexture.CreateFullscreenUI("names", true, scene);
  }

  private build(id: string, name: string, color: number): Avatar {
    const root = new TransformNode(`remote:${id}`, this.scene);
    const mat = new StandardMaterial(`remote:${id}:mat`, this.scene);
    mat.diffuseColor = new Color3(((color >> 16) & 255) / 255, ((color >> 8) & 255) / 255, (color & 255) / 255);
    mat.specularColor = Color3.Black();
    const shape = avatarShape();
    const body = MeshBuilder.CreateCapsule(`remote:${id}:body`, { radius: BODY_RADIUS, height: shape.bodyHeight }, this.scene);
    body.position.y = shape.bodyY;
    body.material = mat;
    body.isPickable = false;
    body.parent = root;
    const head = MeshBuilder.CreateBox(`remote:${id}:head`, { size: shape.head }, this.scene);
    head.position.y = shape.headY;
    head.material = mat;
    head.isPickable = false;
    head.parent = root;
    this.onMeshes?.([body, head], true);
    const label = new Rectangle();
    label.height = "22px"; label.width = "140px"; label.thickness = 0; label.background = "rgba(0,0,0,0.45)"; label.cornerRadius = 4;
    const text = new TextBlock(); text.text = name; text.color = "white"; text.fontSize = 13;
    label.addControl(text);
    this.gui.addControl(label);
    label.linkWithMesh(head);
    label.linkOffsetY = -30;
    return { root, buf: new InterpBuffer(), label, name };
  }

  upsert(p: PlayerState, now: number): void {
    let a = this.avatars.get(p.id);
    if (!a) {
      a = this.build(p.id, p.displayName, p.color);
      this.avatars.set(p.id, a);
    }
    if (p.pos) a.buf.push({ t: now, x: p.pos.x, y: p.pos.y, z: p.pos.z, yaw: p.yaw });
  }

  remove(id: string): void {
    const a = this.avatars.get(id);
    if (!a) return;
    a.label.dispose();
    this.onMeshes?.(a.root.getChildMeshes(), false);
    a.root.getChildMeshes().forEach((m) => (m as Mesh).dispose());
    a.root.dispose();
    this.avatars.delete(id);
  }

  clear(): void { for (const id of [...this.avatars.keys()]) this.remove(id); }
  count(): number { return this.avatars.size; }

  render(now: number): void {
    const t = now - RENDER_DELAY;
    for (const a of this.avatars.values()) {
      const s = a.buf.sample(t);
      if (!s) continue;
      a.root.position.set(s.x, s.y, s.z);
      a.root.rotation.y = (s.yaw * Math.PI) / 180;
      a.buf.prune(now - 1000);
    }
  }
}
